<template>
	<span>
		<div v-if="loading" class="flex flex-col w-full h-52 items-center justify-center splash-screen-loading-box">
			<n-spin :size="80" />
		</div>

		<div v-else class="page">
			<n-form
				ref="formRef"
				:model="storeCommonSense.activityConfiguration"
				:rules="storeCommonSense.activityFormRules"
				:size="size"
				label-placement="top"
			>
				<n-grid :x-gap="24" :cols="24">
					<n-grid-item :span="12">
						<n-form-item-gi :label="$t('gm.dashboards.challenges')" path="challenges">
							<n-transfer
								v-model:value="storeCommonSense.activityConfiguration.challenges"
								style="width: 100%"
								:options="storeCommonSense.challengeOptions"
								:disabled="!storeCommonSense.activityConfiguration.setting"
							/>
						</n-form-item-gi>
					</n-grid-item>

					<n-grid-item :span="12">
						<div class="flex flex-col gap-3">
							<n-form-item-gi :label="$t('gm.dashboards.type')" path="setting">
								<n-select
									v-model:value="storeCommonSense.activityConfiguration.setting"
									:placeholder="$t('gm.dashboards.placeholder_type')"
									:options="storeCommonSense.settingsOptions"
									@update:value="onSettingSelected"
								/>
							</n-form-item-gi>

							<CommonSenseSettingSummary
								v-if="storeCommonSense.selectedSetting"
								:setting="storeCommonSense.selectedSetting"
							/>
						</div>
					</n-grid-item>
				</n-grid>
			</n-form>
		</div>

		<Dialog
			v-model:visible="showLogoDialog"
			modal
			:header="t('gm.dashboards.setting_logo_replace_title')"
			class="w-full max-w-md"
			:closable="!logoDialogLoading"
			:close-on-escape="!logoDialogLoading"
			@hide="onLogoDialogHide"
		>
			<div class="flex items-start gap-3 py-2">
				<i class="pi pi-exclamation-triangle shrink-0 text-3xl text-amber-500" aria-hidden="true" />

				<p class="m-0 text-slate-700">{{ t("gm.dashboards.setting_logo_replace_message") }}</p>
			</div>

			<template #footer>
				<div class="flex flex-wrap justify-end gap-2">
					<Button
						type="button"
						severity="secondary"
						outlined
						:label="t('gm.dashboards.setting_logo_keep_event')"
						:disabled="logoDialogLoading"
						@click="resolveLogoDialog('keep')"
					/>

					<Button
						type="button"
						severity="primary"
						class="bg-primary"
						:label="t('gm.dashboards.setting_logo_use_setting')"
						:loading="logoDialogLoading"
						@click="resolveLogoDialog('apply')"
					/>
				</div>
			</template>
		</Dialog>
	</span>
</template>

<script lang="ts" setup>
import { NForm, NFormItemGi, NGrid, NGridItem, NSelect, NSpin, NTransfer } from "naive-ui"
import type { FormInst } from "naive-ui"

import { onMounted, ref } from "vue"

import { useRoute } from "vue-router"

import { useI18n } from "vue-i18n"

import Button from "primevue/button"

import Dialog from "primevue/dialog"

import CommonSenseSettingSummary from "@/customComponents/common_sense/CommonSenseSettingSummary.vue"

import { useCommonSenseStore } from "@/stores/apps/common_sense"

import type { Size } from "naive-ui/es/form/src/interface"

const storeCommonSense = useCommonSenseStore()

const route = useRoute()

const { t } = useI18n()

const loading = ref(true)

const formRef = ref<FormInst | null>(null)

const size = ref<Size | undefined>("medium")

const showLogoDialog = ref(false)

const logoDialogLoading = ref(false)

let logoDialogResolve: ((choice: "keep" | "apply") => void) | null = null

function waitForLogoConflictChoice(): Promise<"keep" | "apply"> {
	return new Promise(resolve => {
		logoDialogResolve = resolve

		showLogoDialog.value = true
	})
}

function resolveLogoDialog(choice: "keep" | "apply") {
	if (choice === "apply") {
		logoDialogLoading.value = true
	}
	showLogoDialog.value = false
	logoDialogResolve?.(choice)
	logoDialogResolve = null
}

function onLogoDialogHide() {
	if (logoDialogResolve) {
		resolveLogoDialog("keep")
	}
}

async function onSettingSelected() {
	storeCommonSense.onSettingChange()
	try {
		await storeCommonSense.applySettingLogoToEvent(waitForLogoConflictChoice)
	} finally {
		logoDialogLoading.value = false
	}
}

onMounted(async () => {
	const apiKey = route.params.api_key ? String(route.params.api_key) : undefined

	await storeCommonSense.loadActivitySetup(apiKey, formRef)

	loading.value = false
})
</script>
